import {
  showPreClearance,
  editPreClearance,
  createPreClearance,
  storePreClearance,
  updatePreClearance,
} from '@app/api/pre-clearance.api';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { notificationController } from '@app/controllers/notificationController';
import { PreClearanceRequest } from '@app/models/pre_clearance/PreClearanceRequest';
import { Form, notification, Tabs, Input, Select } from 'antd';
import { Tab } from 'rc-tabs/lib/interface';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PreClearanceInfo from './PreClearanceInfo';
import AdditionalInfo from './AdditionalInfo';
import CommentsTab from '../common/preClearance/Comments';
import DocumentChecklist from '../shared/DocumentChecklist';
import { DropdownOption } from '@app/models/DropdownOption';
import { getVerifiedCustomers } from '@app/api/customer.api';
import { Customer } from '@app/models/customer/Customer';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { useTranslation } from 'react-i18next';
import { useHeader } from '@app/components/header/layouts/HeaderContext';
import { CustomerInfoForm } from '@app/components/forms/CustomerInfoForm/CustomerInfoForm';
import GenerateDocumentsButton from './GenerateDocuments';
import CertificateList from '@app/components/apps/PreClearance/AllPRCertificates';
import Configuration from './Configuration';
import Extension from './Extention';
import WorkflowButtons from './WorkFlowButtons';
import { CustomerDTO } from '@app/models/customer/CustomerDTO';
import { PreClearanceFile } from '@app/models/pre_clearance/PreClearanceResponse';
import ExtensionInfo from './ExtensionInfo';

interface TypeOfForm {
  type: 'edit' | 'create' | 'view';
}

interface PreClearanceFormProps extends TypeOfForm {
  editable: boolean;
  id?: string;
  showEditIcon?: boolean;
  pageTitle: string;
  // eslint-disable-next-line @typescript-eslint/ban-types
  setCurrentBreadcrumName: Function;
}

const PreClearanceForm: React.FC<PreClearanceFormProps> = ({
  editable = false,
  id,
  pageTitle,
  type,
  setCurrentBreadcrumName,
}) => {
  const [form] = Form.useForm();
  const [isLoading, setLoading] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const navigate = useNavigate();
  const preClearanceId = parseInt(id ?? '');
  const [referenceCode, setReferenceCode] = useState<string>('');
  const [code, setCode] = useState<string>('');
  const [comments, setComments] = useState([]);
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [location_options, setLocationOptions] = useState<DropdownOption[]>([]);
  const [countries, setCountries] = useState<DropdownOption[]>([]);
  const [department_options, setDepartmentOptions] = useState<DropdownOption[]>([]);
  const [productCategories, setProductCategories] = useState<DropdownOption[]>([]);
  const [specialConditions, setSpecialConditions] = useState('');
  const [acceptedFields, setAccepetedFields] = useState([]);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userType = user?.usertype;
  const { t } = useTranslation();
  const { addHeaderButton, removeHeaderButton } = useHeader();
  const [wfActions, setWfActions] = useState<string[]>([]);
  const [ExtensionwfActions, setExtensionWfActions] = useState<string[]>([]);
  const [DocFormFields, setDocFormFields] = useState<any[]>([]);
  const [IniDocFormFields, setIniDocFormFields] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<string>('1');
  const [preClearanceDetails, setPreClearanceDetails] = useState({
    job_id: '',
    job_type: '',
    status: '',
  });
  const [hasLetter, setHasLetter] = useState(false);
  const [hasCertificate, setHasCertificate] = useState(false);
  const [hasAnnex, setHasAnnex] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [updateConfigInfo, setUpdateConfigInfo] = useState(false);
  const [customerData, setCustomerInfo] = useState<CustomerDTO | null>(null);
  const [extensionExpiryDate, setExtensionExpiryDate] = useState<string | null>(null);
  const [statusRefreshKey, setStatusRefreshKey] = useState(0);
  const [mappedFields, setMappedFields] = useState<any[]>([]);
  const [additionalInfo, setAdditionalInfo] = useState<Record<string, any>>({});
  const [preClearanceFile, setPreClearanceFile] = useState<PreClearanceFile | PreClearanceFile[] | null>(null);
  const [preClearanceExtension, setPreClearanceExtension] = useState<any>(null);
  const [canExtend, setCanExtend] = useState(false);

  const shouldShowEditButton = () => {
    if (userType === 'ADMIN') {
      return;
    } else if (userType === 'CUSTOMER') {
      return status === 'DRAFT' || status === 'PENDING_DOCUMENT';
    }
    return false;
  };

  const items: Tab[] | undefined = [
    {
      key: '1',
      label: 'Customer Info',
      children: <CustomerInfoForm form={form} type={type} customerInfo={customerData} createType="PC" />,
    },
    {
      key: '2',
      label: 'Pre-Clearance Info',
      children: (
        <PreClearanceInfo
          status={status}
          editable={editable}
          canEdit={canEdit}
          type={type}
          isStatusDoReview={status == 'DOREVIEW'}
          acceptedFields={acceptedFields}
          productCategories={productCategories}
          setAcceptedFields={setAccepetedFields}
          countries={countries}
          department_options={department_options}
          location_options={location_options}
          form={form}
          preClearanceData={undefined}
          preClearanceDetails={{
            job_id: code, // Assign the actual pre-clearance ID
            job_type: productCategories.map((category) => category.label).join(', '), // Assign the actual pre-clearance category names as a comma-separated string
            status: status ?? '',
          }}
        />
      ),
    },
    ...(status !== null && status !== 'DRAFT'
      ? [
          {
            key: '3',
            label: t('Document Checklist'),
            children: (
              <DocumentChecklist
                editable={editable}
                initialformFields={IniDocFormFields}
                formFields={DocFormFields}
                status={status}
                pre_clearance_id={id ?? null}
              />
            ),
          },
        ]
      : []),
    ...(user?.id !== 4 && status !== 'DRAFT'
      ? [
          {
            key: '4',
            label: 'Additional Info',
            children: (
              <AdditionalInfo
                //selectedCategoryId={form.getFieldValue('pre_clearance_category_id')}
                //preClearanceId={preClearanceId}
                editable={editable}
                fields={mappedFields}
                additionalInfo={additionalInfo}
              />
            ),
          },
        ]
      : []),
    ...(user?.id == 2 && status !== 'DRAFT' && status !== 'PENDING_DOCUMENT' && status !== 'PENDING_REVIEW'
      ? [
          {
            key: '5',
            label: 'Configuration Info',
            children: (
              <Configuration
                editable={editable}
                updateConfigInfo={updateConfigInfo}
                isStatusDoReview={status == 'DOREVIEW'}
                acceptedFields={acceptedFields}
                setAcceptedFields={setAccepetedFields}
                form={form}
                preClearanceData={undefined}
                extensionExpire={extensionExpiryDate}
                pre_clearance_id={id ?? null}
                status={status}
              />
            ),
          },
        ]
      : []),
    ...(status == 'DONE' || status == 'PENDING_APPROVAL'
      ? [
          {
            key: '6',
            label: t('Documents'),
            children: <CertificateList id={id ?? null} files={preClearanceFile} />,
          },
        ]
      : []),
    ...(status == 'DONE' && preClearanceExtension
      ? [
          {
            key: '7',
            label: t('Extension'),
            children: <ExtensionInfo extension={preClearanceExtension} />,
          },
        ]
      : []),
    ...(type !== 'create' && status !== 'DRAFT'
      ? [
          {
            key: '8',
            label: t('Comments'),
            children: (
              <CommentsTab editable={editable} status={status} commentData={comments} pre_clearance_id={id ?? null} />
            ),
          },
        ]
      : []),
  ];

  function setCustomerData(customer: Customer) {
    setCustomerId(customer?.id);
    form.setFieldsValue({
      id: customer.id,
      email: customer.user?.email || '',
      name: `${customer.first_name || ''} ${customer.last_name || ''}`.trim(),
      status: customer.user?.status || '',
      customer_type: customer.customer_type || '',
      contact_no: customer.contact_no || '',
      address: customer.address || '',
      nic: customer.nic || '',
      dob: customer.dob || '',
    });
  }
  // const getCustomerData = (email: string) => {
  //   if (user?.id === 4 && user?.customer) {
  //     // Map CustomerModel → Customer
  //     const customerAsCustomer: Customer = {
  //       id: user.customer.id,
  //       user_id: user.customer.user_id ?? user.id, // fallback
  //       customer_type: user.customer.customer_type || 'One Time',
  //       first_name: user.customer.first_name || '',
  //       last_name: user.customer.last_name || '',
  //       contact_no: user.customer.contact_no || '',
  //       address: user.customer.address || '',
  //       dob: user.customer.dob || '',
  //       nic: user.customer.nic || '',
  //       name: `${user.customer.first_name || ''} ${user.customer.last_name || ''}`.trim(),
  //       user: {
  //         id: user.id,
  //         email: user.email || '',
  //         name: '',
  //         email_verified_at: undefined,
  //         created_at: '',
  //         updated_at: '',
  //         username: '',
  //         usertype: '',
  //         image_url: '',
  //         customer: {
  //           id: 0,
  //           user_id: 0,
  //           customer_type: '',
  //           first_name: '',
  //           last_name: '',
  //           contact_no: '',
  //           address: '',
  //           dob: '',
  //           nic: '',
  //           verified: false,
  //           created_at: '',
  //           updated_at: '',
  //           name: `${user.customer.first_name || ''} ${user.customer.last_name || ''}`.trim(),
  //           additional_info: undefined,
  //           user: undefined,
  //         },
  //         status: '',
  //       },
  //       verified: false,
  //       created_at: '',
  //       updated_at: '',
  //     };

  //     setCustomerData(customerAsCustomer);
  //     return;
  //   }

  //   // Normal flow for staff/admin
  //   getVerifiedCustomers(email)
  //     .then((res: any) => {
  //       setCustomer(res);
  //       setCustomerData(res);
  //     })
  //     .catch((error: any) => {
  //       notificationController.error({
  //         message: error.message,
  //       });
  //     });
  // };

  // useEffect(() => {
  //   async function fetchPreClearanceData() {
  //     try {
  //       const preClearanceData = await editPreClearance(Number(id)); // Fetch data
  //       console.log('Full editPreClearance data:', preClearanceData); // Debugging

  //       if (preClearanceData?.pre_clearance?.customer) {
  //         const customer = preClearanceData.pre_clearance.customer || JSON.parse(localStorage.getItem('user') || '{}');
  //         console.log('Customer Data:', customer);
  //         settCustomerData(customer);

  //         // Extract email & status from customer.user (if available)
  //         if (customer.user) {
  //           setCustomerEmail(customer.user.email);
  //           setCustomerStatus(customer.user.status);
  //           console.log('Customer Email:', customer.user.email);
  //           console.log('Customer Status:', customer.user.status);
  //         }
  //       }
  //     } catch (error) {
  //       console.error('Error fetching pre-clearance data:', error);
  //     }
  //   }

  //   fetchPreClearanceData();
  // }, [editPreClearance, id]);

  // useEffect(() => {
  //   const fetchData = async () => {
  //     if (type !== 'edit') return; // Only fetch for edit type
  //     try {
  //       const result = await editPreClearance(Number(id));

  //       // Check if result and necessary properties exist before setting state
  //       if (result?.pre_clearance?.pre_clearance_extension?.extension_exp) {
  //         setExtensionExpiryDate(result.pre_clearance.pre_clearance_extension.extension_exp);
  //       } else {
  //         console.warn('No extension expiry date found.');
  //       }
  //     } catch (error) {
  //       console.error('Error fetching pre-clearance data:', error);
  //     }
  //   };

  //   if (id) {
  //     fetchData();
  //   }
  // }, [id]);

  const setApiData = (res: any) => {
    const referenceCode = res?.pre_clearance?.reference_code ?? 'New';
    const breadcrumbName = `${referenceCode}`;
    setCurrentBreadcrumName(breadcrumbName);
    setCode(res?.pre_clearance?.id ?? 'N/A');
    setReferenceCode(res?.pre_clearance?.reference_code ?? 'N/A');
    setStatus(res?.pre_clearance?.status ?? 'New');
    setAccepetedFields(res?.pre_clearance?.accepted);
    setSpecialConditions(res?.pre_clearance?.additionalInfo?.special_conditions ?? '');
    setCountries(res?.countries);
    setComments(res?.pre_clearance?.pre_clearance_comments ?? []);
    setDepartmentOptions(res?.department_options ?? []);
    setLocationOptions(res?.location_options ?? []);
    setProductCategories(res?.pre_clearance_category_options);
    setCustomerId(res?.pre_clearance?.customer?.id ?? user?.customer?.id);
    setIniDocFormFields(res?.pre_clearance?.pre_clearance_documents || []);
    setDocFormFields(res.pre_clearance?.pre_clearance_category?.pre_clearance_collections || []);
    setWfActions(res?.wf_actions ?? []);
    // setExtentionExpire(res?.PreClearance?.pre);
    setPreClearanceDetails({
      job_id: res?.pre_clearance?.id ?? 'N/A',
      job_type: res?.pre_clearance_category_options?.map((category: any) => category.label).join(', ') ?? '',
      status: res?.pre_clearance?.status ?? '',
    });

    //console.log('generates raw:', res?.pre_clearance?.generates);
    //console.log('LETTER value:', res?.pre_clearance?.generates?.LETTER, typeof res?.pre_clearance?.generates?.LETTER);

    setHasLetter(res?.generates?.LETTER ?? false);
    setHasCertificate(res?.generates?.CERTIFICATE ?? false);
    setHasAnnex(res?.generates?.EXTENSION ?? false);
    setCustomerInfo(res?.pre_clearance?.customer ?? null);
    setCanEdit(res?.pre_clearance?.editable_for_usertype ?? false);
    setCanExtend(res?.pre_clearance?.can_extend ?? false);
    setUpdateConfigInfo(res?.pre_clearance?.can_edit_config_info ?? false);

    const fieldsData = res?.pre_clearance?.pre_clearance_category?.pre_clearance_fields || [];

    const mapped: any[] = fieldsData.map((field: any) => ({
      key: field.field_id,
      label: field.name,
      type: field.input_type?.toLowerCase() as 'text' | 'number' | 'dropdown' | 'richtext',
      options: field.options ? field.options.map((opt: string) => ({ value: opt, label: opt })) : undefined,
    }));

    setMappedFields(mapped);
    setAdditionalInfo(res?.pre_clearance?.pre_clearance_info?.informations || {});
    setPreClearanceExtension(res?.pre_clearance?.pre_clearance_extension ?? null);
    setPreClearanceFile(res?.pre_clearance?.pre_clearance_files ?? null);
    setExtensionWfActions(res?.extention_wf_actions ?? []);

    if (res?.pre_clearance?.customer) {
      setCustomerData(res?.pre_clearance?.customer);
    } else {
      setCustomerData({
        id: user.customer.id,
        user: {
          email: user.email || '',
          status: user.status || '',
          id: 0,
          name: '',
          email_verified_at: undefined,
          created_at: '',
          updated_at: '',
          username: '',
          usertype: '',
          image_url: '',
          customer: {
            pr_options: [],
            pc_options: [],
            id: 0,
            user_id: 0,
            customer_type: '',
            first_name: '',
            last_name: '',
            contact_no: '',
            address: '',
            dob: '',
            nic: '',
            verified: false,
            created_at: '',
            updated_at: '',
            name: '',
            additional_info: undefined,
            user: undefined,
          },
        },
        customer_type: user.customer.customer_type || '',
        contact_no: user.customer.contact_no || '',
        address: user.customer.address || '',
        nic: user.customer.nic || '',
        dob: user.customer.dob || '',
        user_id: 0,
        first_name: user.customer.first_name || '',
        last_name: user.customer.last_name || '',
        verified: false,
        created_at: '',
        updated_at: '',
        name: `${user.customer.first_name || ''} ${user.customer.last_name || ''}`.trim(),
        pr_options: [],
        pc_options: [],
      });
    }

    form.setFieldsValue({
      reference_code: res?.pre_clearance?.reference_code ?? '',
      status: res?.pre_clearance?.status,
      location_id: res?.pre_clearance?.location_id,
      department_id: res?.pre_clearance?.department_id,
      pre_clearance_category_id: res?.pre_clearance?.pre_clearance_category_id,
      customer_id: customerId,
      invoice_no: res?.pre_clearance?.invoice_no,
      invoice_date: res?.pre_clearance?.invoice_date,
      product_name: res?.pre_clearance?.product_name,
      quantity: res?.pre_clearance?.quantity,
      hs_code: res?.pre_clearance?.hs_code,
      value_usd: res?.pre_clearance?.value_usd,
      supplier_name: res?.pre_clearance?.supplier_name,
      supplier_address: res?.pre_clearance?.supplier_address,
      supplier_reg: res?.pre_clearance?.supplier_reg,
      accepted: acceptedFields,
      country_ids: res?.pre_clearance?.countries?.map((e: any) => e?.id) ?? [],
      validity_period: res?.pre_clearance?.validity_period,
      validity_exp: res?.pre_clearance?.validity_exp,
      special_conditions: res?.pre_clearance?.special_conditions,
      allowed_weightage: res?.pre_clearance?.allowed_weightage,
      expires_at: res?.pre_clearance?.expires_at,
      certificate_name: res?.pre_clearance?.certificate_name,
      letter_name: res?.pre_clearance?.letter_name,
      extension_name: res?.pre_clearance?.extension_name,
    });
  };

  useEffect(() => {
    setLoading(true);

    const fetchData = async () => {
      try {
        const res =
          preClearanceId && type === 'view'
            ? await showPreClearance(preClearanceId)
            : preClearanceId && type === 'edit'
            ? await editPreClearance(preClearanceId)
            : type === 'create'
            ? await createPreClearance()
            : null;

        if (res) setApiData(res);
      } catch (err: any) {
        notification.error({ message: 'Failed to fetch product data', description: err.message });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [preClearanceId, type, status, id]);

  const handleSubmit = (values: PreClearanceRequest) => {
    const data = form.getFieldsValue();

    if (editable) {
      const submissionData = {
        ...data,
        ...values,
        reference_code: String(referenceCode),
        customer_id: customerId,
        accepted: acceptedFields ?? [],
        expires_at: form.getFieldValue('expires_at') || '',
        location_id: form.getFieldValue('location_id') || null,
        department_id: form.getFieldValue('department_id') || null,
        pre_clearance_category_id: form.getFieldValue('pre_clearance_category_id') || null,
        invoice_no: form.getFieldValue('invoice_no') || '',
        invoice_date: form.getFieldValue('invoice_date') || '',
        product_name: form.getFieldValue('product_name') || '',
        quantity: form.getFieldValue('quantity') || 0,
        hs_code: form.getFieldValue('hs_code') || '',
        value_usd: form.getFieldValue('value_usd') || 0,
        supplier_name: form.getFieldValue('supplier_name') || '',
        supplier_address: form.getFieldValue('supplier_address') || '',
        supplier_reg: form.getFieldValue('supplier_reg') || '',
        validity_period: form.getFieldValue('validity_period') || '',
        validity_exp: form.getFieldValue('validity_exp') || '',
        special_conditions: form.getFieldValue('special_conditions') || '',
        allowed_weightage: form.getFieldValue('allowed_weightage') || 0,
      };

      if (type === 'create') {
        storePreClearance(submissionData)
          .then((res: any) => {
            setLoading(false);
            navigate(`/view-pre-clearance`, { state: { id: res?.data?.id } });
          })
          .catch((err: any) => {
            notificationController.error({ message: err.message });
            setLoading(false);
          });
      } else if (type === 'edit' && preClearanceId) {
        updatePreClearance(preClearanceId, submissionData)
          .then(() => {
            setLoading(false);
            navigate(`/view-pre-clearance`, { state: { id: id } });
          })
          .catch((err: any) => {
            notificationController.error({ message: `${err.message}` });
            setLoading(false);
          });
      }
    }
    console.log('Form Submitted');
  };

  const hasAnyDocument = hasLetter || hasCertificate || hasAnnex;

  useEffect(() => {
    const buttons = {
      'save-button': editable && (
        <BaseButton
          type="primary"
          loading={isLoading}
          size="small"
          onClick={() => {
            form
              .validateFields()
              .then((values) => {
                handleSubmit(values);
              })
              .catch((error) => {
                console.error('Validation failed:', error);
              });
          }}
        >
          {t('Save')}
        </BaseButton>
      ),
      'cancel-button': editable && (
        <BaseButton onClick={() => navigate('/pre-clearance')} size="small" style={{ marginRight: '8px' }}>
          {t('Cancel')}
        </BaseButton>
      ),
      'edit-button': type === 'view' && preClearanceId && shouldShowEditButton() && (
        <BaseButton
          type="primary"
          onClick={() => navigate('/edit-pre-clearance', { state: { id: id } })}
          size="small"
          style={{ marginRight: '8px' }}
        >
          {t('Edit')}
        </BaseButton>
      ),
      'generate-documents': type === 'view' && preClearanceId && hasAnyDocument && (
        <GenerateDocumentsButton
          hasLetter={hasLetter}
          hasCertificate={hasCertificate}
          hasAnnex={hasAnnex}
          onRefresh={refreshPreClearance}
          id={preClearanceId}
        />
      ),
      'request-extension': type === 'view' && preClearanceId && (
        <Extension
          preClearanceId={preClearanceId}
          userType={user?.usertype}
          preClearanceExtension={preClearanceExtension}
          canExtend={canExtend}
          ExtensionwfActions={ExtensionwfActions}
        />
      ),
    };

    //console.log('request-extension button value:', buttons['request-extension']);

    Object.entries(buttons).forEach(([key, button]) => {
      if (button) addHeaderButton(key, button);
    });

    return () => {
      Object.keys(buttons).forEach(removeHeaderButton);
    };
  }, [
    type,
    status,
    preClearanceId,
    editable,
    addHeaderButton,
    removeHeaderButton,
    t,
    navigate,
    isLoading,
    form,
    handleSubmit,
    id,
    preClearanceDetails,
    hasLetter,
    hasCertificate,
    hasAnnex,
    hasAnyDocument,
    ExtensionwfActions,
  ]);

  const refreshPreClearance = async () => {
    try {
      const res =
        preClearanceId && type === 'view'
          ? await showPreClearance(preClearanceId)
          : preClearanceId && type === 'edit'
          ? await editPreClearance(preClearanceId)
          : null;

      if (res) {
        setApiData(res);
      }
    } catch (err: any) {
      notification.error({
        message: 'Failed to fetch product data',
        description: err.message,
      });
    }
  };

  useEffect(() => {
    setLoading(true);

    refreshPreClearance().finally(() => {
      setLoading(false);
    });
  }, [preClearanceId, type]);

  return (
    <>
      <BaseRow gutter={[16, 16]}>
        <BaseCol span={24}>
          <WorkflowButtons
            id={id ?? null}
            key={statusRefreshKey}
            status={status}
            setStatus={setStatus}
            wfActions={wfActions || []}
          />
        </BaseCol>

        <BaseCol span={24}>
          <BaseCard id="PreClearanceForm" title={pageTitle} padding="1.25rem">
            <BaseRow>
              {/* {user?.usertype != 'CUSTOMER' && type == 'create' && (
                <BaseCol span={12}>
                  <Search enterButton="Search" size="small" onSearch={(e: any) => getCustomerData(e)} />
                </BaseCol>
              )} */}
              <BaseCol span={24}>
                <Form
                  form={form}
                  name="PreClearanceForm"
                  onFinish={handleSubmit}
                  labelCol={{ lg: { span: 6 }, md: { span: 6 }, sm: { span: 24 } }}
                  labelAlign="left"
                  labelWrap
                  wrapperCol={{ lg: { span: 16 }, md: { span: 18 }, sm: { span: 24 } }}
                  size="small"
                >
                  <Tabs items={type == 'create' ? items?.slice(0, 2) : items} />
                </Form>
              </BaseCol>
              <BaseCol span={12}></BaseCol>
            </BaseRow>
          </BaseCard>
        </BaseCol>
      </BaseRow>
      {/* <BaseRow>
        <BaseCol
          xs={24}
          lg={8}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            justifyContent: 'flex-end', // Align to the right
            marginLeft: 'auto', // This also ensures it aligns on the right
            marginRight: 0, // Optional: helps to ensure no extra space on the right
          }}
        >
          <br />
          <h2 style={{ color: '#333B69', fontWeight: 'bold' }}>Job Info</h2>
          <div
            style={{
              padding: '1rem',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              backgroundColor: '#fff',
              width: '100%',
              marginTop: '-10px',
            }}
          >
            <div style={{ marginLeft: '1rem' }}>
              <div>
                <p style={{ color: '#B1B1B1' }}>Category:</p>
                <p style={{ margin: 0, color: '#C5C5CF' }}>{preClearanceDetails.job_id}</p>
              </div>

              <div>
                <p style={{ color: '#B1B1B1' }}>Job Type:</p>
                <Select
                  size="small"
                  style={{
                    width: '70%',
                    fontSize: '12px',
                  }}
                  disabled
                  value={preClearanceDetails.job_type}
                  options={[
                    { value: 'Full-time', label: 'Full-time' },
                    { value: 'Part-time', label: 'Part-time' },
                    { value: 'Contract', label: 'Contract' },
                  ]}
                />
              </div>

              <div>
                <p style={{ color: '#B1B1B1' }}>Status:</p>
                <p style={{ margin: 0, color: '#C5C5CF' }}>{preClearanceDetails.status}</p>
              </div>
            </div>
          </div>
        </BaseCol>
      </BaseRow> */}
    </>
  );
};

export default PreClearanceForm;
